/** @velyn-custom BEGIN — vely-featured-collection.js (NPS-style bleed + slidesOffsetBefore when carousel; pill pagination; grid fallback) */
(function () {
  const getSwiper = () => window.Swiper;
  const MOBILE_MQ = "(max-width: 767.98px)";
  const TABLET_MQ = "(min-width: 768px) and (max-width: 1024.98px)";
  const WIDE_MQ = "(min-width: 1600px)";
  const CARD_MIN = 275;

  class VelyFeaturedCollection extends HTMLElement {
    /**
     * Left inset = header .container content start (pad + center gutter).
     * slidesOffsetBefore/After move with the track — free-bleed on slide, no jump.
     * After mirrors before on desktop so the last slide ends at container right.
     * Matches NPS measure strategy; container ladder: 1rem / 5vw / ≥1800: 1600px pad 0.
     */
    getLeftInsetPx() {
      const section = this.closest(".section-vely-featured-collection");
      const shell = this.querySelector(".vely-fc__slider-shell");
      if (!section || !shell) return 0;

      const titleBox = section.querySelector(
        ".vely-fc__header-wrap, .vely-fc__section-inner > .container, .vely-fc__section-inner > .full-width"
      );
      const shellLeft = shell.getBoundingClientRect().left;

      if (titleBox) {
        const pad =
          parseFloat(getComputedStyle(titleBox).paddingInlineStart) || 0;
        return Math.max(
          0,
          Math.round(titleBox.getBoundingClientRect().left + pad - shellLeft)
        );
      }

      const probe = document.createElement("div");
      probe.setAttribute("aria-hidden", "true");
      probe.style.cssText =
        "position:absolute;visibility:hidden;pointer-events:none;height:0;width:var(--vely-fc-left-inset);";
      section.appendChild(probe);
      const px = Math.round(probe.getBoundingClientRect().width) || 0;
      probe.remove();
      return Math.max(0, px);
    }

    pinLastSnapToEnd(swiper = this._swiper) {
      if (!swiper?.snapGrid?.length) return;
      const maxSnap = -swiper.maxTranslate();
      const last = swiper.snapGrid.length - 1;
      if (Number.isFinite(maxSnap) && swiper.snapGrid[last] !== maxSnap) {
        swiper.snapGrid[last] = maxSnap;
      }
    }

    /**
     * Mobile carousel: 1rem from the left only (track still bleeds right).
     * Tablet/desktop: measured container gutters on both ends.
     */
    getTrackOffsets() {
      if (!this.isCarouselActiveForViewport()) {
        return { before: 0, after: 0 };
      }
      if (window.matchMedia(MOBILE_MQ).matches) {
        const rootSize =
          parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        return { before: Math.round(rootSize), after: 0 };
      }
      const inset = this.getLeftInsetPx();
      return { before: inset, after: inset };
    }

    isTablet() {
      return window.matchMedia(TABLET_MQ).matches;
    }

    isWide() {
      return window.matchMedia(WIDE_MQ).matches;
    }

    isMobile() {
      return window.matchMedia(MOBILE_MQ).matches;
    }

    currentGap() {
      const raw = this.isMobile()
        ? this.dataset.spacingMobile
        : this.dataset.spacing;
      const gap = Number(raw);
      return Number.isFinite(gap) ? gap : 0;
    }

    rowColumns() {
      const raw = this.isTablet() ? this.dataset.tablet : this.dataset.desktop;
      const cols = Math.floor(Number(raw) || 1);
      return Math.max(cols, 1);
    }

    slideEls(swiper) {
      if (swiper?.slides?.length) return Array.from(swiper.slides);
      return Array.from(this.querySelectorAll(".swiper-slide"));
    }

    /**
     * Real site .container (90% from 768, max 1600px from 1800).
     */
    measureContainer() {
      const section = this.closest(".section-vely-featured-collection");
      if (!section) return null;

      const probe = document.createElement("div");
      probe.className = "container";
      probe.setAttribute("aria-hidden", "true");
      probe.style.cssText =
        "height:0;overflow:hidden;visibility:hidden;pointer-events:none;";
      section.appendChild(probe);
      const rect = probe.getBoundingClientRect();
      const style = getComputedStyle(probe);
      const padStart = parseFloat(style.paddingInlineStart) || 0;
      const padEnd = parseFloat(style.paddingInlineEnd) || 0;
      probe.remove();

      return {
        left: rect.left + padStart,
        width: Math.max(0, rect.width - padStart - padEnd),
      };
    }

    countThatFit(containerWidth, gap, minWidth) {
      if (containerWidth <= 0) return 1;
      let count = 0;
      while ((count + 1) * minWidth + count * gap <= containerWidth + 0.5) {
        count += 1;
      }
      return Math.max(count, 1);
    }

    clearSizedLayout(swiper) {
      const hadSizedLayout = this.classList.contains("vely-fc--sized");
      this.classList.remove(
        "vely-fc--contained",
        "vely-fc--slide",
        "vely-fc--sized",
        "vely-fc--wrap"
      );
      this.style.removeProperty("--vely-fc-card-w");
      this.style.removeProperty("--vely-fc-gap");
      this.style.removeProperty("--vely-fc-share");
      this.style.removeProperty("--vely-fc-fit");
      if (!hadSizedLayout) return;

      this.querySelectorAll(".swiper-slide").forEach((slide) => {
        slide.style.width = "";
      });
      if (swiper) this.restoreBreakpointParams(swiper);
    }

    restoreBreakpointParams(swiper) {
      const isMobile = window.matchMedia(MOBILE_MQ).matches;
      const desktopSlides = Number(this.dataset.desktop) || 4;
      const mobileSlides = Number(this.dataset.mobile) || 1.5;
      const gapDesktop = Number(this.dataset.spacing) || 0;
      const gapMobile = Number(this.dataset.spacingMobile) || 0;
      const carouselOn = isMobile
        ? this.dataset.carouselMobile === "true"
        : this.dataset.carouselDesktop === "true";

      swiper.params.slidesPerView = isMobile ? mobileSlides : desktopSlides;
      swiper.params.spaceBetween = isMobile ? gapMobile : gapDesktop;
      if (carouselOn && swiper.enabled === false) swiper.enable();
      if (!carouselOn && swiper.enabled !== false) swiper.disable();
      swiper.update();
    }

    paintSlides(swiper, slides, gap, contained) {
      slides.forEach((slide) => {
        slide.style.width = "";
      });

      swiper.params.slidesPerView = "auto";
      swiper.params.spaceBetween = 0;
      swiper.params.slidesOffsetBefore = 0;
      swiper.params.slidesOffsetAfter = 0;

      const shouldSlide = !contained && slides.length > 1;
      if (swiper.enabled === false) swiper.enable();
      if (!shouldSlide) swiper.slideTo?.(0, 0);
      swiper.update();
      if (!shouldSlide) swiper.disable();
      if (shouldSlide) this.pinLastSnapToEnd(swiper);
      this.syncControls(swiper);
    }

    /**
     * Same fit/slide rule as the product icon box.
     * Cards stay at least 275px. If the row fits in .container, it stays there.
     * If not, the first card aligns to the container and the track bleeds right.
     * Single row, when it fits: only the configured column count is shown.
     */
    applyFitOrSlide(swiper, singleRow) {
      const gap = this.currentGap();
      const slides = this.slideEls(swiper);
      const count = slides.length;
      if (!count) return;

      const box = this.measureContainer();
      const containerWidth = box?.width || 0;
      const fitCount = this.countThatFit(containerWidth, gap, CARD_MIN);
      const rowCount = singleRow ? Math.min(count, this.rowColumns()) : count;
      const contained = rowCount <= fitCount;
      const columns = contained ? rowCount : fitCount;

      this.classList.toggle("vely-fc--contained", contained);
      this.classList.toggle("vely-fc--slide", !contained);
      this.classList.remove("vely-fc--wrap");
      this.classList.add("vely-fc--sized");
      this.style.setProperty("--vely-fc-share", String(columns));
      this.style.setProperty("--vely-fc-fit", String(columns));
      this.style.setProperty("--vely-fc-gap", gap + "px");

      this.paintSlides(swiper, slides, gap, contained);
    }

    /**
     * Viewport >=1600, carousel off, single row: the visible cards share
     * .container. Staying inside the container wins over the 275px floor.
     */
    applyContainedColumns(swiper) {
      const gap = this.currentGap();
      const slides = this.slideEls(swiper);
      const count = slides.length;
      if (!count) return;

      const columns = Math.min(count, this.rowColumns());

      this.classList.add("vely-fc--contained", "vely-fc--sized");
      this.classList.remove("vely-fc--slide", "vely-fc--wrap");
      this.style.setProperty("--vely-fc-share", String(columns));
      this.style.setProperty("--vely-fc-gap", gap + "px");
      this.style.removeProperty("--vely-fc-card-w");

      this.paintSlides(swiper, slides, gap, true);
    }

    /**
     * Carousel off + wrap: stay in .container. Cards are at least 275px and
     * extra products flow onto the next row.
     */
    applyWrap(swiper) {
      const gap = this.currentGap();
      const slides = this.slideEls(swiper);
      const count = slides.length;
      if (!count) return;

      const box = this.measureContainer();
      const containerWidth = box?.width || 0;
      const fitCount = this.countThatFit(containerWidth, gap, CARD_MIN);
      const columns = Math.min(count, this.rowColumns(), fitCount);

      this.classList.add("vely-fc--contained", "vely-fc--sized", "vely-fc--wrap");
      this.classList.remove("vely-fc--slide");
      this.style.setProperty("--vely-fc-share", String(columns));
      this.style.setProperty("--vely-fc-gap", gap + "px");
      this.style.removeProperty("--vely-fc-card-w");

      slides.forEach((slide) => {
        slide.style.width = "";
      });

      swiper.params.slidesPerView = "auto";
      swiper.params.spaceBetween = 0;
      swiper.params.slidesOffsetBefore = 0;
      swiper.params.slidesOffsetAfter = 0;
      if (swiper.enabled === false) swiper.enable();
      swiper.slideTo?.(0, 0);
      swiper.update();
      swiper.disable();
      this.syncControls(swiper);
    }

    /**
     * Mobile keeps the section settings: Items per row on mobile, and a 1rem
     * left inset when the mobile carousel is on. The 275px fit/slide rule
     * starts at tablet.
     */
    applyMobileLayout(swiper) {
      this.clearSizedLayout(swiper);
      const mobileSlides = Number(this.dataset.mobile) || 1.5;
      const gapMobile = Number(this.dataset.spacingMobile) || 0;
      const carouselMobile = this.dataset.carouselMobile === "true";
      const { before, after } = this.getTrackOffsets();

      swiper.params.slidesPerView = mobileSlides;
      swiper.params.spaceBetween = gapMobile;
      swiper.params.slidesOffsetBefore = before;
      swiper.params.slidesOffsetAfter = after;
      if (carouselMobile && swiper.enabled === false) swiper.enable();
      if (!carouselMobile && swiper.enabled !== false) swiper.disable();
      swiper.update();
      if (carouselMobile) this.pinLastSnapToEnd(swiper);
      this.syncControls(swiper);
    }

    applyViewportLayout(swiper = this._swiper) {
      if (!swiper) return;
      const carouselDesktop = this.dataset.carouselDesktop === "true";

      if (this.isMobile()) {
        this.applyMobileLayout(swiper);
        return;
      }

      if (!this.isMobile() && carouselDesktop && this.isWide()) {
        this.clearSizedLayout(swiper);
        this.applyTrackOffsets(swiper);
        return;
      }

      if (carouselDesktop) {
        this.applyFitOrSlide(swiper, false);
        return;
      }

      if (!this.classList.contains("vely-fc--single-row")) {
        this.applyWrap(swiper);
        return;
      }

      if (this.isWide()) {
        this.applyContainedColumns(swiper);
        return;
      }

      this.applyFitOrSlide(swiper, true);
    }

    applyTrackOffsets(swiper = this._swiper) {
      if (!swiper) return;
      const carouselActive = this.isCarouselActiveForViewport();
      const { before, after } = this.getTrackOffsets();

      if (
        swiper.params.slidesOffsetBefore === before &&
        swiper.params.slidesOffsetAfter === after
      ) {
        if (carouselActive) {
          swiper.updateSize?.();
          swiper.updateSlides?.();
          this.pinLastSnapToEnd(swiper);
        }
        return;
      }
      swiper.params.slidesOffsetBefore = before;
      swiper.params.slidesOffsetAfter = after;
      swiper.update();
      this.pinLastSnapToEnd(swiper);
    }

    connectedCallback() {
      if (this._velyFcInitStarted) return;
      this._velyFcInitStarted = true;
      this.waitForSwiper(() => this.init());
    }

    disconnectedCallback() {
      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }
      this._paginationEl = null;
      this._velyFcInitStarted = false;
      this.classList.remove("vely-fc--ready");
    }

    waitForSwiper(callback, attempt = 0) {
      const hasSwiper = typeof getSwiper() === "function";
      const hasPagination = Boolean(window.VelySwiperPagination?.refresh);
      if (hasSwiper && hasPagination) {
        callback();
        return;
      }
      if (attempt > 80) {
        console.warn(
          "[vely-featured-collection] Swiper or VelySwiperPagination not available"
        );
        return;
      }
      setTimeout(() => this.waitForSwiper(callback, attempt + 1), 50);
    }

    isCarouselActiveForViewport() {
      if (this.classList.contains("vely-fc--slide")) return true;
      const isMobile = window.matchMedia(MOBILE_MQ).matches;
      if (isMobile) {
        return this.dataset.carouselMobile === "true";
      }
      return this.dataset.carouselDesktop === "true";
    }

    refreshPagination() {
      const pagination = window.VelySwiperPagination;
      if (!pagination || !this._swiper || !this._paginationEl) return;

      const slideCount = this._swiper.slides ? this._swiper.slides.length : 0;
      const bulletCount = this._paginationEl.querySelectorAll(
        ".swiper-pagination-bullet, .bullet"
      ).length;
      if (slideCount > 0 && bulletCount !== slideCount) {
        this._paginationEl.innerHTML = "";
      }

      pagination.refresh(this._swiper, this._paginationEl);
    }

    syncControls(swiper) {
      const controls = this.querySelector("[data-vely-pagination-anchor]");
      if (!controls || !swiper) return;

      this.refreshPagination();

      const carouselActive = this.isCarouselActiveForViewport();
      const shouldShow =
        carouselActive &&
        swiper.enabled !== false &&
        (window.VelySwiperPagination?.shouldShowPagination?.(swiper) ?? false);
      controls.hidden = !shouldShow;
    }

    init() {
      const SwiperCtor = getSwiper();
      if (typeof SwiperCtor !== "function") return;

      const swiperEl = this.querySelector(".vely-fc__swiper");
      if (!swiperEl) return;

      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }

      this._paginationEl = this.querySelector(".vely-swiper-pagination-pill");

      const itemDesktop = Number(this.dataset.desktop) || 4;
      const itemTablet = Number(this.dataset.tablet) || itemDesktop;
      const itemMobile = Number(this.dataset.mobile) || 1.5;
      const gapDesktop = Number(this.dataset.spacing) || 0;
      const gapMobile = Number(this.dataset.spacingMobile) || 0;
      const useLoop = this.dataset.loop === "true";
      const carouselDesktop = this.dataset.carouselDesktop === "true";
      const carouselMobile = this.dataset.carouselMobile === "true";
      const { before: leftInset, after: rightInset } = this.getTrackOffsets();

      const swiperConfig = {
        slidesPerView: itemMobile,
        spaceBetween: gapMobile,
        speed: 300,
        loop: useLoop,
        grabCursor: true,
        watchSlidesProgress: true,
        observer: true,
        observeParents: true,
        enabled: carouselMobile,
        slidesOffsetBefore: leftInset,
        slidesOffsetAfter: rightInset,
        // Swiper 11 crashes on `pagination: false`. Omit pagination; bullets
        // are built manually via VelySwiperPagination (same as NPS).
        breakpoints: {
          768: {
            slidesPerView: itemTablet,
            spaceBetween: gapDesktop,
            enabled: carouselDesktop,
          },
          1025: {
            slidesPerView: itemDesktop,
            spaceBetween: gapDesktop,
            enabled: carouselDesktop,
          },
        },
        on: {
          init: (swiper) => {
            window.VelySwiperPagination?.pinInFlow?.(this._paginationEl);
            this.applyViewportLayout(swiper);
            requestAnimationFrame(() => this.syncControls(swiper));
          },
          resize: (swiper) => {
            this.applyViewportLayout(swiper);
            this.syncControls(swiper);
          },
          breakpoint: (swiper) => {
            this.applyViewportLayout(swiper);
            this.syncControls(swiper);
          },
          slidesLengthChange: (swiper) => {
            this.syncControls(swiper);
          },
          slideChange: (swiper) => {
            if (!this.isCarouselActiveForViewport() || !this._paginationEl) {
              return;
            }
            window.VelySwiperPagination?.setActiveBullet?.(
              swiper,
              this._paginationEl
            );
          },
          slideChangeTransitionEnd: (swiper) => {
            if (!this.isCarouselActiveForViewport() || !this._paginationEl) {
              return;
            }
            window.VelySwiperPagination?.setActiveBullet?.(
              swiper,
              this._paginationEl
            );
          },
          progress: (swiper) => {
            if (!this.isCarouselActiveForViewport() || !this._paginationEl) {
              return;
            }
            window.VelySwiperPagination?.setActiveBullet?.(
              swiper,
              this._paginationEl
            );
          },
          reachEnd: (swiper) => {
            if (!this.isCarouselActiveForViewport() || !this._paginationEl) {
              return;
            }
            window.VelySwiperPagination?.setActiveBullet?.(
              swiper,
              this._paginationEl
            );
          },
        },
      };

      try {
        this._swiper = new SwiperCtor(swiperEl, swiperConfig);
      } catch (error) {
        this._swiper = null;
        console.warn("[vely-featured-collection] Swiper init failed", error);
        return;
      }

      window.VelySwiperPagination?.pinInFlow?.(this._paginationEl);
      requestAnimationFrame(() => {
        this.applyViewportLayout(this._swiper);
        this.syncControls(this._swiper);
      });
      this.syncControls(this._swiper);

      this.classList.add("vely-fc--ready");
    }
  }

  if (!customElements.get("vely-featured-collection")) {
    customElements.define("vely-featured-collection", VelyFeaturedCollection);
  }

  document.addEventListener("shopify:section:load", (event) => {
    const host = event.target?.querySelector?.("vely-featured-collection");
    if (host) {
      host._velyFcInitStarted = false;
      host.connectedCallback();
    }
  });
})();
/** @velyn-custom END — vely-featured-collection.js */
